'use client';

import type { Tables } from '@root/database';
import { Settings } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DeleteConfirmDialog } from '@/components/ui/DeleteConfirmDialog';
import { deleteTournament } from '@/lib/tournaments/actions';
import { TournamentDialog } from './TournamentDialog';
interface TournamentsListProps {
  games: Tables<'game'>[];
  tournaments: ({
    game: Tables<'game'> | null;
  } & Tables<'tournaments'>)[];
}
export const TournamentsList = ({
  games,
  tournaments
}: TournamentsListProps) => {
  const router = useRouter();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteTournament(id);
      toast.success('Tournoi supprimé avec succès');
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la suppression du tournoi');
    } finally {
      setDeletingId(null);
    }
  };
  const handleRowClick = (tournamentId: string) => {
    router.push(`/tournaments/${tournamentId}/view`);
  };
  const getStatusLabel = (tournament: {
    game: Tables<'game'> | null;
  } & Tables<'tournaments'>) => {
    if (tournament.status === 'pending' && tournament.started_at) {
      return 'En pause';
    }
    switch (tournament.status) {
      case 'finished':
        return 'Terminé';
      case 'pending':
        return 'À venir';
      case 'started':
        return 'En cours';
      default:
        return 'À venir';
    }
  };
  const getStatusColor = (tournament_0: {
    game: Tables<'game'> | null;
  } & Tables<'tournaments'>) => {
    if (tournament_0.status === 'pending' && tournament_0.started_at) {
      return 'bg-orange-100 text-orange-800';
    }
    switch (tournament_0.status) {
      case 'finished':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'started':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };
  return <div className="w-full space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Tournois</h2>
        <TournamentDialog games={games} />
      </div>

      <div className="w-full rounded-lg border border-white/20 bg-white shadow-lg overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Nom
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Jeu
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Lieu
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Statut
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                  Public
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {tournaments.length === 0 ? <tr>
                  <td className="px-4 py-8 text-center text-gray-500" colSpan={7}>
                    Aucun tournoi pour le moment
                  </td>
                </tr> : tournaments.map(tournament_1 => <tr key={tournament_1.id} className="hover:bg-gray-50 transition-colors cursor-pointer" role="button" tabIndex={0} onClick={() => handleRowClick(tournament_1.id)} onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleRowClick(tournament_1.id);
              }
            }}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {tournament_1.name}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {tournament_1.game ? `${tournament_1.game.name} (${tournament_1.game.console})` : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {tournament_1.location || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {new Date(tournament_1.starts_at).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(tournament_1)}`}>
                        {getStatusLabel(tournament_1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {tournament_1.is_public ? 'Oui' : 'Non'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1" role="presentation" onClick={event_0 => event_0.stopPropagation()} onKeyDown={event_1 => event_1.stopPropagation()}>
                        <Link href={`/tournaments/${tournament_1.id}`}>
                          <Button aria-label="Gérer les joueurs" size="icon" title="Gérer les joueurs" variant="ghost">
                            <Settings />
                          </Button>
                        </Link>
                        <TournamentDialog games={games} tournament={tournament_1} />
                        <DeleteConfirmDialog description="Cette action est irréversible. Le tournoi sera définitivement supprimé." isLoading={deletingId === tournament_1.id} title="Supprimer le tournoi ?" onConfirm={() => handleDelete(tournament_1.id)} />
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};